import {
	ATTRIBUTES,
} from '@/scripts/core/global';
import { type StickyHeader } from '@/scripts/theme/sticky-header'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { SELECTORS } from '@/scripts/core/global'
import {
	CartAddWithSections,
	renderRawHTMLToDOM,
} from '@/scripts/core/cart-functions'

export class CartNotification extends UcoastEl {
	static htmlSelector = 'cart-notification'
	static selectors = {
		notification: '[data-uc-cart-notification]',
		closeButton: '[data-uc-cart-notification-close]',
		heading: '[data-uc-cart-notification-heading]',
		body: '[data-uc-cart-notification-body]',
	}
	notification: HTMLElement
	heading: HTMLElement
	body: HTMLElement
	header: StickyHeader
	onBodyClick: (event: MouseEvent) => void
	activeElement?: HTMLElement
	defaultHeading: string
	defaultAriaLabel: string
	sectionApiIds = ['cart-notification-product', 'cart-icon-bubble','cart-notification-button']
	constructor() {
		super()

		this.notification = q.rs(CartNotification.selectors.notification)
		this.heading = q.rs(CartNotification.selectors.heading, this.notification)
		this.body = q.rs(CartNotification.selectors.body, this.notification)
		this.header = q.rs('sticky-header')
		this.onBodyClick = this.handleBodyClick.bind(this)
		this.defaultHeading = this.heading.textContent?.trim() ?? ''
		this.defaultAriaLabel =
			this.notification.getAttribute('aria-label') ?? this.defaultHeading

		this.notification.addEventListener(
			'keyup',
			(event) => event.code === 'Escape' && this.close()
		)
		this.querySelectorAll(CartNotification.selectors.closeButton).forEach((closeButton) =>
			closeButton.addEventListener('click', this.close.bind(this))
		)
	}

	open() {
		const isAlreadyActive = this.notification.classList.contains('active')
		this.notification.classList.add('animate', 'active')
		if (isAlreadyActive) {
			this.notification.focus()
			window.TsDOM.trapFocus(this.notification)
			document.body.addEventListener('click', this.onBodyClick)
			return
		}

		this.notification.addEventListener(
			'transitionend',
			() => {
				this.notification.focus()
				window.TsDOM.trapFocus(this.notification)
			},
			{ once: true }
		)

		document.body.addEventListener('click', this.onBodyClick)
	}

	close() {
		this.notification.classList.remove('active')
		document.body.removeEventListener('click', this.onBodyClick)

		window.TsDOM.removeTrapFocus(this.activeElement)
	}

	renderContents(cart: CartAddWithSections) {
		this.setMode('success')
		this.setActiveElement(document.activeElement)
		this.getSectionsToRender().forEach((section) => {
			const sectionId = section.id
			if (!sectionId) throw new Error('Section id is required')
			renderRawHTMLToDOM({
				sourceHTML: cart.sections[sectionId],
				sourceSelector: section.selector,
				destinationSelector: section.selector,
			})
		})

		if (cart.items.length) {
			this.removeAttribute(ATTRIBUTES.cartEmpty)
		} else {
			this.setAttribute(ATTRIBUTES.cartEmpty, '')
		}

		if (this.header) this.header.reveal()
		this.open()
	}

	showError(message = window.cartStrings.error) {
		this.setMode('error', message)
		if (this.header) this.header.reveal()
		this.open()
	}

	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'cart-notification-product',
				selector: `#cart-notification-product`,
			},
			{
				id: 'cart-notification-button',
				selector: '#cart-notification-button'
			},
			{
				id: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
		]
	}

	handleBodyClick(event: MouseEvent) {
		const target = q.rt(event)
		const closestCartNotification = q.oc<CartNotification>(
			target,
			'cart-notification'
		)
		if (target !== this.notification && !closestCartNotification) {
			const disclosure = q.oClosestTarget(event, 'details-disclosure, header-menu')
			this.activeElement = disclosure ? q.rs('summary', disclosure) : undefined
			this.close()
		}
	}

	setActiveElement(element: Element | null) {
		if (!element) throw new Error('Active element is required')
		this.activeElement = element as HTMLElement
	}

	setMode(mode: 'success' | 'error', message = '') {
		this.notification.dataset.state = mode
		if (mode === 'success') {
			this.heading.textContent = this.defaultHeading
			this.notification.setAttribute('aria-label', this.defaultAriaLabel)
			this.body.textContent = ''
			this.body.hidden = true
			return
		}

		this.heading.textContent = window.cartStrings.errorTitle
		this.notification.setAttribute('aria-label', window.cartStrings.errorTitle)
		this.body.textContent = message
		this.body.hidden = false
	}
}
